import { writable } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';
import { getProject, addProject, updateProject, generateProject } from '../api/data-handler.js';
import { getCustomer, addCustomer, updateCustomer, generateCustomer } from '../api/data-handler.js';
import { getUser, addUser, updateUser, generateUser } from '../api/data-handler.js';
import { userName } from '../../helpers/helpers.js';
import { logError } from '../logging/log-handler.js';

const mockCustomerData = {
    customer: {
        list: [
            { customer_id: "1", name: "Customer One", status: "active" },
            { customer_id: "2", name: "Customer Two", status: "active" },
            { customer_id: "3", name: "Customer Three", status: "active" },
            { customer_id: "4", name: "Customer Four", status: "active" }
        ]
    },
    user_type: {
        list: [
            {
                id: "oh-user",
                name: "OH User",
                role: {
                    list: [
                        {
                            id: "editor",
                            name: "Editor",
                            claims: {
                                canViewResults: true,
                                canManageProjects: true,
                                canManageCustomers: false,
                                canManageUsers: false,
                                canLoadProjectDocuments: true
                            },
                            "customer-relationship": "multiple"
                        }
                    ]
                }
            }
        ]
    }
};

export const drawerStore = writable({
    instanceId: uuidv4(),
    open: false,
    objectType: null as 'customer' | 'project' | 'user' | null,
    action: null as 'add' | 'edit' | null,
    id: null as string | null,
    object: null as any
});

export async function addObject(type: 'customer' | 'project' | 'user', parent_id?: string) {
    let object;
    if (type === 'customer') {
        object = await generateCustomer();
    } else if (type === 'project') {
        object = await generateProject(parent_id || '');
    } else if (type === 'user') {
        object = await generateUser();
    }
    drawerStore.set({ 
        instanceId: uuidv4(), 
        open: true, 
        objectType: type, 
        action: 'add', 
        id: null, 
        object 
    });
}

export async function editObject(type: 'customer' | 'project' | 'user', id: string) {
    let object;
    if (type === 'customer') {
        object = await getCustomer(id);
    } else if (type === 'project') {
        object = await getProject(id);
    } else if (type === 'user') {
        object = await getUser(id);
    }
    
    if (!object) {
        logError(new Error(`${type} with id ${id} not found`), "object-drawer-store", undefined, { operation: "editObject", type, id });
    } else {
        drawerStore.set({ 
            instanceId: uuidv4(), 
            open: true, 
            objectType: type, 
            action: 'edit', 
            id,
            object 
        });
    }
}

export async function saveObject(object: any) {
    drawerStore.update(store => {
        if (store.objectType === 'customer') {
            if (store.action === 'add') {
                addCustomer(object).then(() => {
                    let objectMutated = {...object, projects: 0, projects_with_results: 0, created_at: new Date().toISOString(), created_by_name: userName()  }; 
                    drawerStore.update(store => ({ ...store, open: false, object: objectMutated }) );
                });
            } else {              
                updateCustomer(object).then(() => {
                    let objectMutated = {...object, code: object.code, name: object.name, updated_at: new Date().toISOString(), updated_by_name: userName() };
                    drawerStore.update(store => ({ ...store, object: objectMutated, open: false }) );
                })
            }
        } else if (store.objectType === 'project') {
            if (store.action === 'add') {
                addProject(object).then(() => {
                    let objectMutated = {...object, created_at: new Date().toISOString(), created_by_name: userName(), last_upload: { version: 0  }, current_upload: { document_count: 0} };
                    drawerStore.update(store => ({ ...store, open: false, object: objectMutated }) );
                });
            } else {
                const customerCode = store.object?.project?.customer?.code;
                const projectInput = {
                    ...object,
                    customer: {
                        customer_id: object.customer_id,
                        code: customerCode
                    }
                };
            
                updateProject(projectInput).then(() => {
                    let objectMutated = {...object, name: object.name, updated_at: new Date().toISOString(), updated_by_name: userName() };
                    drawerStore.update(store => ({ ...store, object: objectMutated, open: false }) );
                });
            }
        } else if (store.objectType === 'user') {
            if (store.action === 'add') {
                addUser(object).then(() => {
                    drawerStore.update(store => ({ ...store, object, open: false }));
                }).catch(error => {
                logError(error, "object-drawer-store", undefined, { operation: "addUser", object });
                });
            } else if (store.action === 'edit') {
                updateUser(object).then(() => {
                    drawerStore.update(store => ({ ...store, object, open: false }));
                }).catch(error => {
                    logError(error, "object-drawer-store", undefined, { operation: "updateUser", object });
                });
            }
        }
        return { ...store, open: false };
    });
}

export function closeDrawer() {
    drawerStore.update(store => ({ ...store, open: false }));
}

export async function generateUserData() {
    return {
        data: {
            user: {
                user_id: uuidv4(),
                name: "",
                email: "",
                oid: "",
                user_type: "",
                user_role: "",
                claims: {},
                metadata: {},
                customers: []
            },
            lookups: mockCustomerData
        }
    };
}